/**
 * Anlage Ballsets — die Seite: Vorschau, Bestaetigung, Lauf, Ergebnis.
 *
 * Eine Variantennummer rein, die vollstaendige Vorschau raus: Original, geplanter Artikel,
 * Set-Varianten-Matrix, Warnungen und die geschaetzte Zahl der Plenty-Writes. Dieser Aufruf
 * ist eine gewoehnliche, synchrone Anfrage — sie liest nur.
 *
 * AB HIER SCHREIBT DIE SEITE. Der Knopf „Anlegen" legt einen echten Artikel in einem echten
 * Shop an; es gibt keine Plenty-Sandbox und kein Rueckgaengig. Genau deshalb steht der
 * `BestaetigungsDialog` dazwischen: erst „Jetzt anlegen" startet den Job, der Knopf selbst
 * oeffnet nur den Dialog. Der Lauf ist ein Job (Core-Hook `useJobStatus` + `startJob`), weil er
 * Minuten dauern kann — Plenty erlaubt 75 Schreibvorgaenge pro Stunde und der Client
 * drosselt selbst. Ein eigenes Polling gibt es nicht und darf es nicht geben (E54).
 *
 * Zwei Reiter: „Vorschau" (die Eingabe, der Lauf und sein Ergebnis) und „Historie" (die
 * Laeufe aus dem Protokoll). Die Historie ist Beiwerk — schlaegt ihr Abruf fehl, bleibt die
 * Vorschau bedienbar; ein Programm, das wegen seiner Nebenanzeige nicht mehr arbeitet, waere
 * schlecht gebaut.
 *
 * Der Status-Abruf der Platzhalter-Seite (`GET /status`) faellt hier bewusst weg: Er sollte
 * beweisen, dass die Kette Frontend → Router → Service steht. Das beweist jetzt die Vorschau
 * selbst, und zwar mit Inhalt statt mit einer gruenen Plakette.
 *
 * Alle Pfade stehen OHNE `/api/`-Prefix — der Core-api-Client haengt ihn selbst an
 * (CI-Check `npm run check:api-prefix`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, History, PlusCircle, Search, Table } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import BestaetigungsDialog from '../components/BestaetigungsDialog';
import ErgebnisBlock from '../components/ErgebnisBlock';
import Historie from '../components/Historie';
import SetAuswahl from '../components/SetAuswahl';
import SetMatrixTabelle from '../components/SetMatrixTabelle';
import {
  BestehendesBallsetBlock,
  KopieBlock,
  OriginalBlock,
  WarnungenBlock,
  WriteSchaetzungBlock,
  ZielBlock,
} from '../components/VorschauKarten';
import { zahl } from '../formate';
import type {
  AnlageErgebnis,
  HistorieAntwort,
  Nachkontrolle,
  SetKombinationKey,
  Vorschau,
} from '../typen';

type Reiter = 'vorschau' | 'historie';

const SPIELBALL_HINWEIS =
  'Spielball: immer 5 + 10, Basis ist der Preis ‚ab Menge 4‘ — die Auswahl ist deshalb gesperrt.';

const LAUF_HINWEIS = 'Ein Lauf arbeitet gerade — die Eingaben sind so lange gesperrt.';

export default function MainPage() {
  const [reiter, setReiter] = useState<Reiter>('vorschau');
  const [historie, setHistorie] = useState<HistorieAntwort | null>(null);
  const [historieLaedt, setHistorieLaedt] = useState(false);
  const [variantenNr, setVariantenNr] = useState('');
  const [setKombination, setSetKombination] = useState<SetKombinationKey>('zehn-fuenfzehn');
  const [vorschau, setVorschau] = useState<Vorschau | null>(null);
  /**
   * Die Bestaetigung „Trotzdem anlegen" gilt fuer GENAU die Vorschau, die auf dem Schirm
   * steht. Deshalb wird sie bei jeder neuen Vorschau zurueckgesetzt (Threat T-03-19):
   * sonst gaebe ein Haken, den jemand fuer Artikel A gesetzt hat, spaeter unbemerkt auch
   * Artikel B frei.
   */
  const [trotzdemAnlegen, setTrotzdemAnlegen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [dialogOffen, setDialogOffen] = useState(false);

  /**
   * Welche Art Job laeuft ueber DEN EINEN Job-Hook? „Anlegen" und „Erneut prüfen" teilen
   * sich ihn bewusst (ein zweiter Hook waere ein zweites Polling, E54) — aber ihre
   * Ergebnisse haben verschiedene Form: `AnlageErgebnis` das eine, `{ nachkontrolle, laufId }`
   * das andere. Ohne diese Unterscheidung liefe der Nachkontroll-Bericht in den
   * Ergebnisblock und stuende dort als gruenes „Ballset angelegt" ohne einen einzigen Wert.
   */
  const [laufArt, setLaufArt] = useState<'anlegen' | 'nachkontrolle'>('anlegen');
  /**
   * Das Ergebnis des letzten ANLAGE-Laufs, eingefroren. Es bleibt stehen, waehrend eine
   * Nachkontrolle laeuft (sonst verschwaende der Block unter dem Knopf, den man gerade
   * gedrueckt hat), und sein Bericht wird danach durch den frischen ersetzt.
   */
  const [anlageErgebnis, setAnlageErgebnis] = useState<AnlageErgebnis | null>(null);
  const [anlageJobStatus, setAnlageJobStatus] = useState<string | null>(null);
  const [anlageFehler, setAnlageFehler] = useState<string | null>(null);

  /**
   * Der Lauf als Job. `useJobStatus` nimmt eine JOB-ID, NICHT die Programm-ID — mit `null`
   * startet der Hook still und uebernimmt die ID, die `startJob()` zurueckbekommt (der
   * Fehler, die Programm-ID hineinzugeben, hat `herkunft-zuweisen` am 14.08.2026 eine
   * ewig pollende Oberflaeche gekostet).
   *
   * `startJob` faengt Fehler selbst und legt sie in `jobFehler` ab. Dort landen auch die
   * drei 409-Faelle dieser Route, jeweils mit dem Text des Backends:
   *   `JOB_ALREADY_RUNNING` — der Core-Doppelstart-Schutz (ein anderer `anlegen`-Job laeuft)
   *   `ANLAGE_LAEUFT`       — ein Lauf arbeitet bereits an genau diesem Original
   *   `BALLSET_VORHANDEN`   — laut Protokoll gibt es schon eines (nur mit „Trotzdem anlegen")
   * Deshalb gibt es hier KEINEN eigenen Statuscode-Zweig. Wer einen „fehlenden" ergaenzt,
   * baut die Meldung ein zweites Mal — und die zweite Fassung veraltet.
   */
  const {
    status, isRunning, progress, result, job, error: jobFehler, startJob,
  } = useJobStatus(null);

  /**
   * Die Historie einmal beim Seitenaufruf. Ihr Fehlschlag wird bewusst geschluckt: sie ist
   * Beiwerk, und eine rote Zeile ueber der Eingabe wuerde einen Fehler melden, der die
   * eigentliche Arbeit gar nicht betrifft. Solange die Protokoll-Tabelle fehlt, antwortet
   * das Backend ohnehin mit einer leeren Liste und dem Hinweis „Migration ausstehend".
   */
  const ladeHistorie = useCallback(async () => {
    setHistorieLaedt(true);
    try {
      const res = await api.get('/programmes/anlage-ballsets/historie');
      setHistorie(res.data.data ?? { eintraege: [] });
    } catch {
      /* Historie ist Beiwerk — darf die Seite nicht blockieren */
    } finally {
      setHistorieLaedt(false);
    }
  }, []);

  useEffect(() => { void ladeHistorie(); }, [ladeHistorie]);

  const holeVorschau = useCallback(async () => {
    const nummer = variantenNr.trim();
    if (!nummer || laedt) return;
    setLaedt(true);
    setFehler(null);
    setTrotzdemAnlegen(false);
    try {
      const res = await api.post('/programmes/anlage-ballsets/vorschau', {
        variantenNr: nummer,
        setKombination,
      });
      const daten: Vorschau = res.data.data;
      setVorschau(daten);
      // Beim Spielball entscheidet das Backend, nicht die Auswahl hier. Wurde sie
      // ueberschrieben, zieht die Oberflaeche nach — sonst stuende eine Wahl auf dem
      // Schirm, mit der die angezeigte Vorschau gar nicht gerechnet wurde.
      if (daten.setKombination.erzwungen) setSetKombination(daten.setKombination.key);
    } catch (err: any) {
      setFehler(err.response?.data?.error?.message ?? err.message);
      setVorschau(null);
    } finally {
      setLaedt(false);
    }
  }, [variantenNr, setKombination, laedt]);

  /**
   * Nach dem Lauf: Historie und Vorschau neu laden — GENAU EINMAL je Job. Der Hook meldet
   * `completed`/`failed` bei jedem Render weiter, und ohne die gemerkte Job-ID liefe das
   * Nachladen in einer Schleife (die Vorschau kostet ein Dutzend Plenty-Lesezugriffe).
   *
   * Die Vorschau wird NICHT auf `null` gesetzt — bewusst anders als im Vorbild
   * `herkunft-zuweisen`: Ergebnis und Ausgangslage sollen nebeneinander stehen. Die frisch
   * geladene Vorschau traegt danach `bestehendesBallset`, und der Knopf sperrt sich von
   * selbst (EING-05).
   */
  const nachbereiteterJob = useRef<string | null>(null);
  useEffect(() => {
    const jobId = job?.id ?? null;
    if (!jobId) return;
    if (status !== 'completed' && status !== 'failed') return;
    if (nachbereiteterJob.current === jobId) return;
    nachbereiteterJob.current = jobId;

    if (laufArt === 'nachkontrolle') {
      // Der frische Bericht ersetzt den alten IM angezeigten Ergebnis. Das `resultSummary`
      // dieses Jobs ist `{ nachkontrolle, laufId }` und KEIN `AnlageErgebnis` — es darf den
      // Ergebnisblock deshalb nie als Lauf-Ergebnis erreichen.
      const frisch = (result as { nachkontrolle?: Nachkontrolle } | null)?.nachkontrolle ?? null;
      if (frisch) {
        setAnlageErgebnis((bisher) => (bisher ? { ...bisher, nachkontrolle: frisch } : bisher));
      }
      // Die Historie traegt den Bericht (er steht am LAUF, nicht am Job) und wird neu geladen.
      // Die Vorschau NICHT: sie kostet ein Dutzend Plenty-Lesezugriffe, und eine reine
      // Nachkontrolle hat am Original nichts geaendert.
      void ladeHistorie();
      return;
    }

    setAnlageErgebnis((result as AnlageErgebnis | null) ?? null);
    setAnlageJobStatus(status);
    setAnlageFehler(jobFehler ?? null);
    void ladeHistorie();
    void holeVorschau();
  }, [status, job?.id, laufArt, result, jobFehler, ladeHistorie, holeVorschau]);

  /** Der Klick bestaetigt — gestartet wird GENAU die Vorschau, die auf dem Schirm steht. */
  const starteAnlage = async () => {
    setDialogOffen(false);
    if (!vorschau) return;
    setFehler(null);
    // Der alte Ergebnisblock verschwindet mit dem Start — er gehoert zum vorigen Lauf.
    setLaufArt('anlegen');
    setAnlageErgebnis(null);
    setAnlageJobStatus(null);
    setAnlageFehler(null);
    await startJob('/programmes/anlage-ballsets/anlegen', {
      variantenNr: vorschau.original.variantenNr,
      // Aus der ANGEZEIGTEN Vorschau, nicht aus dem Auswahlfeld: beim Spielball hat das
      // Backend die Kombination erzwungen, und angelegt werden soll das Gezeigte.
      setKombination: vorschau.setKombination.key,
      trotzdem: trotzdemAnlegen,
    });
  };

  /**
   * „Erneut prüfen" — ueber DENSELBEN `useJobStatus`-Hook wie „Anlegen". Kein zweiter Hook,
   * kein eigenes Polling, kein eigener Statuscode-Zweig: ein 409 (`JOB_ALREADY_RUNNING`,
   * es laeuft schon eine Nachkontrolle) landet mit dem Text des Servers in `jobFehler` und
   * damit in derselben Hinweisbox wie die uebrigen Ablehnungen.
   *
   * Der Pfad steht OHNE `/api/`-Prefix — der Core-api-Client haengt ihn selbst an.
   */
  const starteNachkontrolle = async (laufId: number) => {
    setFehler(null);
    setLaufArt('nachkontrolle');
    await startJob(`/programmes/anlage-ballsets/nachkontrolle/${laufId}`, {});
  };

  const spielball = vorschau?.original.istSpielball === true;

  /**
   * Der Doppelanlage-Schutz als EINE Bedingung (EING-05): ein bereits angelegtes Ballset
   * laesst sich mit „Trotzdem anlegen" uebersteuern, ein LAUFENDER Vorgang nicht.
   */
  const gesperrtDurchProtokoll =
    (vorschau?.bestehendesBallset != null && !trotzdemAnlegen)
    || vorschau?.laufendeAnlage != null;

  const reiterKnopf = (wert: Reiter, label: string, Icon: any) => (
    <button
      type="button"
      onClick={() => setReiter(wert)}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition-colors ${
        reiter === wert
          ? 'border-[#1A1A1A] text-[#1A1A1A] font-semibold'
          : 'border-transparent text-gray-500 hover:text-[#1A1A1A]'
      }`}
    >
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <h1 className="text-2xl font-bold text-[#1A1A1A]">Anlage Ballsets</h1>
      <p className="text-sm text-gray-500">
        Variantennummer eines Ball-Artikels eingeben — die Vorschau zeigt, was angelegt würde.
        Es wird nichts geschrieben.
      </p>

      <div className="flex gap-1 border-b border-[#DAD9D9] mt-3 mb-4">
        {reiterKnopf('vorschau', 'Vorschau', Table)}
        {reiterKnopf('historie', 'Historie', History)}
      </div>

      {reiter === 'historie' && (
        <Historie antwort={historie} laedt={historieLaedt} onNeuLaden={() => void ladeHistorie()} />
      )}

      {reiter === 'vorschau' && (
        <>
        <div className="border border-[#DAD9D9] rounded-lg bg-white p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="md:w-72">
              <label className="block text-sm font-medium text-[#1A1A1A]" htmlFor="variantennummer">
                Variantennummer des Ball-Artikels
              </label>
              <input
                id="variantennummer"
                type="text"
                value={variantenNr}
                onChange={(e) => setVariantenNr(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void holeVorschau();
                }}
                disabled={isRunning}
                placeholder="z. B. as-401620"
                className="mt-1 w-full rounded border border-[#DAD9D9] bg-white px-3 py-2 text-sm text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#E9FF57]"
              />
            </div>

            <div className="flex-1">
              <SetAuswahl
                value={setKombination}
                onChange={setSetKombination}
                gesperrt={spielball || isRunning}
                sperrGrund={spielball ? SPIELBALL_HINWEIS : LAUF_HINWEIS}
              />
            </div>

            <button
              type="button"
              onClick={() => void holeVorschau()}
              disabled={laedt || isRunning || variantenNr.trim() === ''}
              className="inline-flex items-center gap-1.5 rounded px-4 py-2 text-sm font-medium bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-50"
            >
              <Search size={14} /> {laedt ? 'Lädt …' : 'Vorschau laden'}
            </button>
          </div>
        </div>

        {/* Auch der Fehler des Job-Hooks gehoert hierher: `startJob` faengt ihn intern ab,
            und ohne diese Zeile blieben die 409-Hinweise (Lauf laeuft, Ballset vorhanden,
            Job laeuft bereits) unsichtbar — der Klick saehe aus wie „nichts passiert". */}
        {(fehler || jobFehler) && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-2 text-sm flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span className="break-words">{fehler ?? jobFehler}</span>
          </div>
        )}

        {isRunning && (
          <div className="mt-4 border border-[#DAD9D9] rounded-lg bg-white p-4">
            {progress ? (
              <>
                <div className="h-2 bg-[#DAD9D9] rounded">
                  <div
                    className="h-2 bg-[#E9FF57] rounded transition-all"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
                <p className="text-xs mt-1.5 text-gray-600">
                  {progress.message ?? 'Lauf gestartet — warte auf den ersten Fortschritt …'}
                </p>
              </>
            ) : (
              <p className="text-xs text-gray-600">
                {laufArt === 'nachkontrolle'
                  ? 'Nachkontrolle läuft — der Artikel wird zurückgelesen …'
                  : 'Lauf gestartet — warte auf den ersten Fortschritt …'}
              </p>
            )}
          </div>
        )}

        {/* Der Block zeigt das eingefrorene Ergebnis des letzten ANLAGE-Laufs. Waehrend einer
            Nachkontrolle bleibt er stehen; ist sie durch, traegt er ihren frischen Bericht.
            `onErneutPruefen` gibt es nur mit bekannter Lauf-Nummer — ohne sie wuesste die
            Route nicht, was sie pruefen soll, und der Knopf waere ein Versprechen ins Leere. */}
        {anlageJobStatus && (
          <div className="mt-4">
            <ErgebnisBlock
              ergebnis={anlageErgebnis}
              jobStatus={anlageJobStatus}
              fehlerText={anlageFehler}
              onErneutPruefen={
                anlageErgebnis?.laufId != null
                  ? () => { void starteNachkontrolle(anlageErgebnis.laufId as number); }
                  : undefined
              }
              laeuft={isRunning}
            />
          </div>
        )}

        {vorschau && !fehler && (
          <div className="mt-4 space-y-4">
            <div className="flex items-baseline gap-2">
              <h2 className="text-lg font-bold text-[#1A1A1A]">{vorschau.ziel.name1}</h2>
              <span className="text-xs text-gray-500">
                {zahl(vorschau.ziel.setVarianten.length)} Set-Varianten · Setgrößen{' '}
                {vorschau.setKombination.setgroessen.join(' + ')}
                {vorschau.setKombination.erzwungen && ' (erzwungen)'}
              </span>
            </div>

            <BestehendesBallsetBlock
              bestehendesBallset={vorschau.bestehendesBallset}
              laufendeAnlage={vorschau.laufendeAnlage}
              trotzdemAnlegen={trotzdemAnlegen}
              onTrotzdemAnlegen={setTrotzdemAnlegen}
            />

            <OriginalBlock original={vorschau.original} />
            <ZielBlock original={vorschau.original} ziel={vorschau.ziel} />
            <SetMatrixTabelle setVarianten={vorschau.ziel.setVarianten} />
            <WarnungenBlock warnungen={vorschau.warnungen} />
            {/* Erst WAS kopiert wird, dann WAS DAS KOSTET — in dieser Reihenfolge, weil die
                Write-Zahl ohne die Liste darueber eine nackte Zahl ohne Bedeutung waere. */}
            <KopieBlock original={vorschau.original} />
            <WriteSchaetzungBlock writesGeschaetzt={vorschau.writesGeschaetzt} />

            {/* Der Knopf oeffnet NUR den Dialog — gestartet wird in `starteAnlage`.
                `gesperrtDurchProtokoll` ist der Doppelanlage-Schutz (EING-05): ein
                bestehendes Ballset laesst sich mit dem Haken uebersteuern, ein laufender
                Vorgang nicht. Wer die Bedingung als ueberfluessig streicht, streicht den
                Schutz. Verbindlich prueft ohnehin die Route noch einmal gegen das
                Protokoll — ein Haken im Browser ist keine Freigabe (Threat T-04-14). */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setDialogOffen(true)}
                disabled={isRunning || !vorschau || gesperrtDurchProtokoll}
                className="inline-flex items-center gap-1.5 rounded px-4 py-2 text-sm font-medium bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlusCircle size={14} /> {isRunning ? 'Läuft …' : 'Anlegen'}
              </button>
              <span className="text-xs text-gray-500">
                Legt einen echten, inaktiven Artikel in Plenty an — nach Bestätigung im Dialog.
              </span>
            </div>
          </div>
        )}

        <BestaetigungsDialog
          offen={dialogOffen}
          vorschau={vorschau}
          trotzdem={trotzdemAnlegen}
          onAbbruch={() => setDialogOffen(false)}
          onBestaetigt={() => { void starteAnlage(); }}
        />
        </>
      )}
    </div>
  );
}
